import { Delete } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { getCompanyConfig } from 'lib/api/company';
import {
  activateCreditRequestWithoutPayouts,
  fetchDetails,
} from 'lib/api/creditRequest';
import { getDocumentsWithArrearCalculation } from 'lib/api/document';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatMoney, normalizeDate } from 'lib/functions/utils';
import ApplicationsInOperationWarnings, {
  ComparisonRow,
} from 'pages/Finance/Components/Financing/common/applicationsInOperationWarnings';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

type PaymentGrouped = {
  amount: number;
  bankAccountDestinationId: string;
  bankAccountOriginId: string;
  cut: string;
};

const availableCuts = (amount: number) => [
  { id: 7000000, name: '$7.000.000' },
  { id: 5000000, name: '$5.000.000' },
  { id: amount, name: 'Sin corte' },
];

const GenerateNomineeCreditRequest = (props: any) => {
  const {
    selectedRequest,
    availableAccounts,
    originAccounts,
    setPayments,
    setSuccessOpen,
    setFailureOpen,
    setSelectedRequest,
    goToSummary,
  } = props;

  const dispatch = useDispatch();

  const amountToDeposit =
    selectedRequest.amount -
    selectedRequest.guaranteeAmount -
    selectedRequest.amountToApply;
  const newPaymentGrouped = {
    bankAccountDestinationId: '',
    bankAccountOriginId: '',
    amount: 0,
    cut: '',
  };

  const [paymentGrouped, setPaymentGrouped] = useState<PaymentGrouped[]>([]);

  const handleAmountChange = (e: any, index: any) => {
    const value = e.target.value.split('.').join('').split('$').join('');
    const newPayments = [...paymentGrouped];
    newPayments[index].amount = parseInt(value);
    setPaymentGrouped(newPayments);
  };

  const amounInNominee = paymentGrouped.reduce(
    (a: number, b: PaymentGrouped) => a + b.amount,
    0,
  );

  const handleGenerateNominee = async () => {
    const paymentGenerated = [];
    for (let i = 0; i < paymentGrouped.length; i++) {
      let remainingAmount = Number(paymentGrouped[i].amount);
      const account = availableAccounts.find(
        (x: any) => x.id === paymentGrouped[i].bankAccountDestinationId,
      );
      const accountOrigin = originAccounts.find(
        (x: any) => x.id === paymentGrouped[i].bankAccountOriginId,
      );
      while (remainingAmount > 0) {
        const amountToDeposit =
          remainingAmount > Number(paymentGrouped[i].cut)
            ? paymentGrouped[i].cut
            : remainingAmount;
        paymentGenerated.push({
          businessName: selectedRequest.businessName,
          bankAccountDestinationId: paymentGrouped[i].bankAccountDestinationId,
          bankAccountOriginId: paymentGrouped[i].bankAccountOriginId,
          bankAccountDestinationName: account.bankName,
          bankAccountDestinationNumber: account.accountNumber,
          bankAccountDestinationAccountType: account.accountType,
          bankAccountDestinationHolderName: account.holderName,
          bankAccountDestinationHolderIdentifier: account.holderIdentifier,
          bankAccountOriginName: accountOrigin.bankName,
          bankName: account.bankName,
          accountNumber: account.accountNumber,
          accountType: account.accountType,
          holderName: account.holderName,
          holderIdentifier: account.holderIdentifier,
          businessIdentifier: account.businessIdentifier,
          amount: amountToDeposit,
          operationId: selectedRequest.id,
          executedAt: null,
        });
        remainingAmount -= Number(paymentGrouped[i].cut);
      }
    }
    setPayments(paymentGenerated);
  };

  const disabled = !availableAccounts.length || !originAccounts.length;

  const handleContinueWithoutNominee = async () => {
    try {
      // Llamar al nuevo servicio para activar sin nómina
      await activateCreditRequestWithoutPayouts(
        dispatch,
        selectedRequest.id,
        new Date(), // executedAt
      );

      if (setSuccessOpen) {
        setSuccessOpen('Solicitud activada correctamente');
      }

      // Recargar el detalle del crédito actualizado
      if (setSelectedRequest) {
        const updatedRequest = await fetchDetails(dispatch, selectedRequest.id);
        setSelectedRequest(updatedRequest);
      }

      // Ir a la pestaña de Resumen
      if (goToSummary) {
        goToSummary();
      }
    } catch (error: any) {
      console.error('Error al activar sin nómina:', error);
      if (setFailureOpen) {
        setFailureOpen(
          error?.message ||
            'Error al activar la solicitud. Por favor intenta nuevamente.',
        );
      }
    }
  };

  const documentsAppliedIds =
    selectedRequest?.documentsFromCreditRequestConciliations?.map(
      (d: { documentId: string }) => d.documentId,
    ) || [];
  const operationDate = new Date(selectedRequest?.operationDate);
  const {
    data: documentsAppliedWithArrearCalculation,
    isLoading: isLoadingDocumentsAppliedWithArrearCalculation,
  } = getDocumentsWithArrearCalculation(
    dispatch,
    documentsAppliedIds,
    operationDate,
  );
  const {
    data: allowOperationIfFutureConciliationsValue,
    isLoading: isLoadingAllowOperationIfFutureConciliations,
  } = getCompanyConfig(dispatch, 'allow_operation_if_future_conciliations');
  const allowOperationIfFutureConciliations =
    allowOperationIfFutureConciliationsValue === '1';

  const isLoading =
    isLoadingDocumentsAppliedWithArrearCalculation ||
    isLoadingAllowOperationIfFutureConciliations;

  const documentsToApply =
    selectedRequest?.documentsFromCreditRequestConciliations ?? [];
  const comparisonRows: ComparisonRow[] =
    documentsAppliedWithArrearCalculation?.documents
      ? documentsToApply.map((doc: { documentId: string; amount?: number }) => {
          const withArrear =
            documentsAppliedWithArrearCalculation.documents.find(
              (d) => d.id === doc.documentId,
            );
          const lastConciliation =
            withArrear?.conciliationLog?.length &&
            withArrear?.conciliationLog?.length > 0
              ? withArrear?.conciliationLog[
                  withArrear?.conciliationLog?.length - 1
                ]
              : null;
          const hasFutureConciliations =
            lastConciliation &&
            normalizeDate(new Date(lastConciliation.conciliationDate)) >
              normalizeDate(operationDate);
          return {
            id: doc.documentId,
            folio: withArrear?.folio ?? doc.documentId,
            amountToConciliate: doc.amount ?? 0,
            totalRemainingNet: withArrear?.totalRemainingNet ?? 0,
            hasFutureConciliations,
          };
        })
      : [];
  const comparisonsWithWarnings = comparisonRows.filter(
    (row) =>
      row.amountToConciliate !== row.totalRemainingNet ||
      row.hasFutureConciliations,
  );
  const hasAmountDifferences = comparisonRows.some(
    (row) => row.amountToConciliate !== row.totalRemainingNet,
  );
  const hasFutureConciliations = comparisonRows.some(
    (row) => row.hasFutureConciliations,
  );
  const hasComparisonWarnings = hasAmountDifferences || hasFutureConciliations;
  const cantContinue =
    hasFutureConciliations && !allowOperationIfFutureConciliations;

  const isActiveOrCompleted =
    selectedRequest.status === 'Activa' ||
    selectedRequest.status === 'Completada';

  if (isLoading) return <CenteredCircularProgress />;
  return (
    <div>
      <Typography fontWeight={600} variant="h6">
        {'Generación nómina'}
      </Typography>
      {documentsAppliedWithArrearCalculation &&
        comparisonRows.length > 0 &&
        !isActiveOrCompleted &&
        hasComparisonWarnings && (
          <ApplicationsInOperationWarnings
            hasAmountDifferences={hasAmountDifferences}
            hasFutureConciliations={hasFutureConciliations}
            allowOperationIfFutureConciliations={
              allowOperationIfFutureConciliations
            }
            comparisonsWithWarnings={comparisonsWithWarnings}
          />
        )}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ padding: '20px 0px', display: 'flex' }}>
          <Typography variant="body1" fontWeight={600}>
            Monto solicitud: {formatMoney(amountToDeposit)}
          </Typography>
          <Typography variant="body1" marginLeft={4} fontWeight={600}>
            Monto en nómina:{' '}
            {formatMoney(
              paymentGrouped.reduce(
                (a: number, b: PaymentGrouped) => a + b.amount,
                0,
              ),
            )}
          </Typography>
        </div>
        {amountToDeposit > 0 && (
          <div style={{ display: 'flex' }}>
            <div style={{ marginLeft: 4 }}>
              <CustomButton
                onClick={() => {
                  setPaymentGrouped([...paymentGrouped, newPaymentGrouped]);
                }}
                color="primary"
                disabled={disabled || cantContinue}
              >
                Agregar
              </CustomButton>
            </div>
            <div style={{ marginLeft: 4 }}>
              <CustomButton
                disabled={
                  formatMoney(amounInNominee) !==
                    formatMoney(amountToDeposit) ||
                  paymentGrouped.some((x: PaymentGrouped) => !x.cut) ||
                  paymentGrouped.some((x: PaymentGrouped) => !x.amount) ||
                  paymentGrouped.some(
                    (x: PaymentGrouped) => !x.bankAccountDestinationId,
                  ) ||
                  paymentGrouped.some(
                    (x: PaymentGrouped) => !x.bankAccountOriginId,
                  ) ||
                  cantContinue
                }
                color="primary"
                onClick={() => handleGenerateNominee()}
              >
                Generar
              </CustomButton>
            </div>
          </div>
        )}
        {amountToDeposit <= 0 && (
          <div style={{ marginLeft: 4 }}>
            <CustomButton
              color="primary"
              onClick={handleContinueWithoutNominee}
              disabled={cantContinue}
            >
              Continuar sin nómina
            </CustomButton>
          </div>
        )}
      </div>
      {amountToDeposit <= 0 && (
        <AlertBanner
          severity="info"
          title="Sin nómina requerida"
          message="El monto total del crédito está aplicado en garantías y/o documentos. No se requiere generar nómina de pago."
          description='Presiona "Continuar sin nómina" para avanzar al siguiente paso.'
        />
      )}
      {disabled && amountToDeposit > 0 ? (
        <Typography variant="body1" padding="20px 0px">
          No tienes cuentas registradas para este cliente. Crea una para poder
          generar una nómina. Puedes hacerlo desde{' '}
          <a href="/network/businesses">acá</a>
        </Typography>
      ) : null}
      {!disabled && paymentGrouped.length === 0 && amountToDeposit > 0 && (
        <Typography variant="body1" padding="20px 0px">
          Agrega transferencias para generar la nómina.
        </Typography>
      )}
      {paymentGrouped.map((x: PaymentGrouped, index: number) => (
        <Grid
          key={index}
          container
          spacing={2}
          alignItems="center"
          rowSpacing={4}
          style={{ padding: '5px 0px' }}
        >
          <Grid item xs={2}>
            <TextField
              fullWidth
              label="Monto"
              value={formatMoney(x.amount)}
              onChange={(e) => handleAmountChange(e, index)}
            />
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <InputLabel id="filter-label">{'Cuenta Origen'}</InputLabel>
              <Select
                labelId="filter-label"
                id="Filtrar por estado"
                fullWidth
                value={paymentGrouped[index].bankAccountOriginId}
                onChange={(e) => {
                  const newPayments = [...paymentGrouped];
                  newPayments[index].bankAccountOriginId = e.target.value;
                  setPaymentGrouped(newPayments);
                }}
                label={'Cuenta bancaria'}
              >
                {originAccounts.map((x: any, index: any) => (
                  <MenuItem key={index} value={x.id}>
                    {x.bankName} ({x.accountNumber})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <InputLabel id="filter-label">{'Cuenta destino'}</InputLabel>
              <Select
                labelId="filter-label"
                id="Filtrar por estado"
                fullWidth
                value={paymentGrouped[index].bankAccountDestinationId}
                onChange={(e) => {
                  const newPayments = [...paymentGrouped];
                  newPayments[index].bankAccountDestinationId = e.target.value;
                  setPaymentGrouped(newPayments);
                }}
                label={'Cuenta bancaria'}
              >
                {availableAccounts.map((x: any, index: any) => (
                  <MenuItem key={index} value={x.id}>
                    {x.bankName} ({x.accountNumber})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <FormControl fullWidth>
              <InputLabel id="filter-label">{'Corte'}</InputLabel>
              <Select
                labelId="filter-label"
                id="Filtrar por estado"
                fullWidth
                value={paymentGrouped[index].cut}
                onChange={(e) => {
                  const newPayments = [...paymentGrouped];
                  newPayments[index].cut = e.target.value;
                  setPaymentGrouped(newPayments);
                }}
                label={'Corte'}
              >
                {availableCuts(paymentGrouped[index].amount).map(
                  (x: any, index: any) => (
                    <MenuItem key={index} value={x.id}>
                      {x.name}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <Grid container>
              <Grid item xs={9}>
                <Typography textAlign={'center'}>
                  {paymentGrouped[index].amount && paymentGrouped[index].cut
                    ? ` N° de trasferencias: ${Math.ceil(
                        paymentGrouped[index].amount /
                          Number(paymentGrouped[index].cut),
                      )}`
                    : 'Complete los campos'}{' '}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <IconButton
                  onClick={() => {
                    const newPayments = [...paymentGrouped];
                    newPayments.splice(index, 1);
                    setPaymentGrouped(newPayments);
                  }}
                >
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ))}
    </div>
  );
};

export default GenerateNomineeCreditRequest;
